import React, { useState } from 'react';
import { Upload, CheckCircle, Clock, AlertCircle, User, Award, FileText, Camera, Phone, Mail, MapPin, Star, Shield } from 'lucide-react';
import { showToast } from '../utils/toast';

interface ExpertVerificationProps {
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const ExpertVerification: React.FC<ExpertVerificationProps> = ({ onClose, onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    email: '',
    phone: '',
    location: '',
    profilePhoto: null as File | null,
    
    // Professional Information
    profession: '',
    specializations: [] as string[],
    licenseNumber: '',
    yearsOfExperience: '',
    currentWorkplace: '',
    
    // Credentials & Documents
    medicalLicense: null as File | null,
    professionalCertificates: [] as File[],
    cv: null as File | null,
    
    // Platform Goals
    platformGoals: [] as string[],
    contentAreas: [] as string[],
    availabilityHours: '',
    
    // Agreement
    termsAccepted: false,
    codeOfConductAccepted: false
  });

  const professions = [
    'Medical Doctor (MD)',
    'Pharmacist',
    'Registered Nurse',
    'Clinical Psychologist',
    'Nutritionist/Dietitian',
    'Physical Therapist',
    'Dentist',
    'Optometrist',
    'Public Health Specialist',
    'Mental Health Counselor'
  ];

  const specializations = [
    'General Medicine',
    'Pediatrics',
    'Cardiology',
    'Dermatology',
    'Mental Health',
    'Nutrition & Diet',
    'Pharmacy Practice',
    'Emergency Medicine',
    'Preventive Care',
    'Women\'s Health',
    'Chronic Disease Management',
    'Infectious Diseases'
  ];

  const platformGoals = [
    'Share trusted health knowledge',
    'Build professional brand',
    'Engage with community',
    'Host live Q&A sessions',
    'Write educational articles',
    'Provide expert consultations',
    'Dispel health misinformation',
    'Support underserved communities'
  ];

  const contentAreas = [
    'Health Education Articles',
    'Live Expert Sessions',
    'Community Q&A',
    'Health Myth Busting',
    'Preventive Care Tips',
    'Medication Guidance',
    'Wellness Coaching',
    'Emergency Health Info'
  ];

  const handleFileUpload = (field: string, file: File) => {
    setFormData(prev => ({ ...prev, [field]: file }));
    showToast.upload.success(file.name);
  };

  const handleMultipleFileUpload = (field: string, files: FileList) => {
    const fileArray = Array.from(files);
    setFormData(prev => ({ ...prev, [field]: [...prev[field as keyof typeof prev] as File[], ...fileArray] }));
    showToast.upload.success(`${files.length} files uploaded`);
  };

  const handleArrayToggle = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field as keyof typeof prev] as string[]).includes(value)
        ? (prev[field as keyof typeof prev] as string[]).filter(item => item !== value)
        : [...(prev[field as keyof typeof prev] as string[]), value]
    }));
  };

  const handleSubmit = () => {
    if (!formData.termsAccepted || !formData.codeOfConductAccepted) {
      showToast.system.error('Please accept all terms and agreements');
      return;
    }

    onSubmit(formData);
    showToast.expert.verificationSubmitted();
    onClose();
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <div key={step} className="flex items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
            currentStep >= step 
              ? 'bg-green-600 text-white scale-110' 
              : 'bg-gray-200 text-gray-600'
          }`}>
            {currentStep > step ? <CheckCircle size={20} /> : step}
          </div>
          {step < 4 && (
            <div className={`w-16 h-1 mx-2 transition-all duration-300 ${
              currentStep > step ? 'bg-green-600' : 'bg-gray-200'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  const Step1PersonalInfo = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Personal Information</h3>
        <p className="text-gray-600">Let's start with your basic information</p>
      </div>

      <div className="flex justify-center mb-6">
        <div className="relative">
          <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
            {formData.profilePhoto ? (
              <img 
                src={URL.createObjectURL(formData.profilePhoto)} 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
            ) : (
              <User size={32} className="text-gray-400" />
            )}
          </div>
          <label className="absolute bottom-0 right-0 w-8 h-8 bg-green-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-green-700 transition-colors">
            <Camera size={16} className="text-white" />
            <input
              type="file"
              accept="image/*"
              onChange={(e) => e.target.files?.[0] && handleFileUpload('profilePhoto', e.target.files[0])}
              className="hidden"
            />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
          <input
            type="text"
            required
            value={formData.fullName}
            onChange={(e) => setFormData({...formData, fullName: e.target.value})}
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Dr. Ama Osei"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="dr.ama@hospital.com"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="+233 24 123 4567"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              required
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Accra, Ghana"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const Step2ProfessionalInfo = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Professional Information</h3>
        <p className="text-gray-600">Tell us about your medical expertise</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Profession *</label>
          <select
            required
            value={formData.profession}
            onChange={(e) => setFormData({...formData, profession: e.target.value})}
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">Select your profession</option>
            {professions.map(profession => (
              <option key={profession} value={profession}>{profession}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">License Number *</label>
          <input
            type="text"
            required
            value={formData.licenseNumber}
            onChange={(e) => setFormData({...formData, licenseNumber: e.target.value})}
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="MDC/12345"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Years of Experience *</label>
          <select
            required
            value={formData.yearsOfExperience}
            onChange={(e) => setFormData({...formData, yearsOfExperience: e.target.value})}
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">Select experience</option>
            <option value="1-2 years">1-2 years</option>
            <option value="3-5 years">3-5 years</option>
            <option value="6-10 years">6-10 years</option>
            <option value="11-15 years">11-15 years</option>
            <option value="15+ years">15+ years</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Current Workplace</label>
          <input
            type="text"
            value={formData.currentWorkplace}
            onChange={(e) => setFormData({...formData, currentWorkplace: e.target.value})}
            className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Korle-Bu Teaching Hospital"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Specializations *</label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {specializations.map((spec) => (
            <button
              key={spec}
              type="button"
              onClick={() => handleArrayToggle('specializations', spec)}
              className={`p-3 text-sm border-2 rounded-xl transition-all duration-200 hover:scale-105 ${
                formData.specializations.includes(spec)
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {spec}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">Select all that apply</p>
      </div>
    </div>
  );

  const Step3Documents = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Verification Documents</h3>
        <p className="text-gray-600">Upload your credentials for verification</p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Medical License *</label>
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gray-400 transition-colors">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => e.target.files?.[0] && handleFileUpload('medicalLicense', e.target.files[0])}
              className="hidden"
              id="medical-license"
            />
            <label htmlFor="medical-license" className="cursor-pointer">
              <Upload className="mx-auto text-gray-400 mb-2" size={32} />
              <p className="text-gray-600">Upload your medical license</p>
              <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (Max 5MB)</p>
            </label>
            {formData.medicalLicense && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 text-sm font-medium">
                  ✓ {formData.medicalLicense.name}
                </p>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Professional Certificates</label>
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gray-400 transition-colors">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              onChange={(e) => e.target.files && handleMultipleFileUpload('professionalCertificates', e.target.files)}
              className="hidden"
              id="certificates"
            />
            <label htmlFor="certificates" className="cursor-pointer">
              <Award className="mx-auto text-gray-400 mb-2" size={32} />
              <p className="text-gray-600">Upload certificates & qualifications</p>
              <p className="text-xs text-gray-500 mt-1">Multiple files allowed</p>
            </label>
            {formData.professionalCertificates.length > 0 && (
              <div className="mt-3 space-y-2">
                {formData.professionalCertificates.map((file, index) => (
                  <div key={index} className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-700 text-sm">✓ {file.name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">CV/Resume</label>
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gray-400 transition-colors">
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(e) => e.target.files?.[0] && handleFileUpload('cv', e.target.files[0])}
              className="hidden"
              id="cv"
            />
            <label htmlFor="cv" className="cursor-pointer">
              <FileText className="mx-auto text-gray-400 mb-2" size={32} />
              <p className="text-gray-600">Upload your CV or resume</p>
              <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX</p>
            </label>
            {formData.cv && (
              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-purple-700 text-sm font-medium">
                  ✓ {formData.cv.name}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const Step4Goals = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Platform Goals & Agreement</h3>
        <p className="text-gray-600">How do you plan to contribute to PharmaPlus?</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Platform Goals *</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {platformGoals.map((goal) => (
            <button
              key={goal}
              type="button"
              onClick={() => handleArrayToggle('platformGoals', goal)}
              className={`p-3 text-sm border-2 rounded-xl transition-all duration-200 hover:scale-105 text-left ${
                formData.platformGoals.includes(goal)
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {goal}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Content Areas *</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {contentAreas.map((area) => (
            <button
              key={area}
              type="button"
              onClick={() => handleArrayToggle('contentAreas', area)}
              className={`p-3 text-sm border-2 rounded-xl transition-all duration-200 hover:scale-105 text-left ${
                formData.contentAreas.includes(area)
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {area}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Availability Hours</label>
        <select
          value={formData.availabilityHours}
          onChange={(e) => setFormData({...formData, availabilityHours: e.target.value})}
          className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="">Select availability</option>
          <option value="1-5 hours/week">1-5 hours per week</option>
          <option value="6-10 hours/week">6-10 hours per week</option>
          <option value="11-20 hours/week">11-20 hours per week</option>
          <option value="20+ hours/week">20+ hours per week</option>
        </select>
      </div>

      <div className="space-y-4 pt-4 border-t border-gray-200">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="terms"
            checked={formData.termsAccepted}
            onChange={(e) => setFormData({...formData, termsAccepted: e.target.checked})}
            className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
          />
          <label htmlFor="terms" className="text-sm text-gray-700">
            I agree to the <span className="text-green-600 font-medium">Terms of Service</span> and understand that my information will be verified by the PharmaPlus team.
          </label>
        </div>

        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="conduct"
            checked={formData.codeOfConductAccepted}
            onChange={(e) => setFormData({...formData, codeOfConductAccepted: e.target.checked})}
            className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
          />
          <label htmlFor="conduct" className="text-sm text-gray-700">
            I agree to follow the <span className="text-green-600 font-medium">Expert Code of Conduct</span> and provide accurate, professional health information.
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Become a PharmaPlus Expert</h2>
            <p className="text-gray-600">Join Ghana's leading health professionals</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          <StepIndicator />

          {currentStep === 1 && <Step1PersonalInfo />}
          {currentStep === 2 && <Step2ProfessionalInfo />}
          {currentStep === 3 && <Step3Documents />}
          {currentStep === 4 && <Step4Goals />}

          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : onClose()}
              className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              {currentStep === 1 ? 'Cancel' : 'Previous'}
            </button>

            {currentStep < 4 ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
              >
                Next Step
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!formData.termsAccepted || !formData.codeOfConductAccepted}
                className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Application
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertVerification;